import * as React from 'react';
import { isSupabase } from '@/lib/supabase';
import { getTenantId, getCourseBySlug, listModules, listLessonsByModule, getLessonById, getModuleBySlug, listCourses, listAllLessons } from '@/repos/contentRepo';
import type { CourseLite, ModuleLite, LessonLite } from '@/types/content';

// Hook centralizado para Tenant ID - evita redundância
export function useTenantId() {
  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    console.log('🔄 useTenantId useEffect:', { isSupabase: isSupabase(), tenantId });
    
    if (!isSupabase()) {
      console.log('⚠️ Não é Supabase, usando tenant padrão');
      setTenantId('default-tenant-id');
      return;
    }
    
    if (tenantId !== null) {
      console.log('✅ Tenant ID já obtido:', tenantId);
      return;
    }
    
    (async () => {
      try {
        console.log('🚀 Iniciando busca de Tenant ID...');
        setLoading(true);
        setError(null);
        const tId = await getTenantId();
        console.log('✅ Tenant ID obtido:', tId);
        setTenantId(tId);
      } catch (e: any) {
        console.error('❌ Erro ao obter Tenant ID:', e);
        setError(e);
        console.log('🔄 Usando tenant padrão devido ao erro');
        setTenantId('default-tenant-id');
      } finally {
        setLoading(false);
        console.log('🏁 useTenantId concluído');
      }
    })();
  }, [tenantId]);

  return { tenantId, loading, error };
}

export function useCourseAndModules(courseSlug?: string) {
  const { tenantId, loading: tenantLoading, error: tenantError } = useTenantId();
  const [course, setCourse] = React.useState<CourseLite | null>(null);
  const [modules, setModules] = React.useState<ModuleLite[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    console.log('🔄 useCourseAndModules useEffect:', { tenantId, courseSlug });
    
    if (!tenantId) {
      console.log('⏳ Aguardando tenantId...');
      return;
    }
    
    if (!courseSlug) {
      console.log('⚠️ courseSlug não fornecido, usando fallback');
      // Fallback para dados mock se não houver courseSlug
      setCourse({
        id: 1,
        slug: 'default',
        title: 'Curso Padrão',
        summary: 'Curso de exemplo',
        status: 'active'
      });
      
      setModules([
        {
          id: 1,
          slug: 'modulo-1',
          title: 'Módulo 1: Introdução',
          summary: 'Módulo de exemplo',
          status: 'active'
        }
      ]);
      return;
    }
    
    (async () => {
      try {
        console.log('🚀 Iniciando busca de curso e módulos...');
        setLoading(true);
        setError(null);
        
        // Buscar curso
        console.log('📚 Buscando curso:', courseSlug);
        const c = await getCourseBySlug(tenantId, courseSlug);
        console.log('✅ Curso encontrado:', c);
        
        // Verificar se o curso tem título válido
        if (!c?.title) {
          console.warn('⚠️ Curso encontrado mas sem título, usando fallback');
          const fallbackTitle = courseSlug ? 
            courseSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
            'Curso Padrão';
          c.title = fallbackTitle;
        }
        
        setCourse(c);
        
        // Buscar módulos
        console.log('📖 Buscando módulos para curso:', c.id);
        const ms = await listModules(tenantId, c.id);
        console.log('✅ Módulos encontrados:', ms);
        setModules(ms);
        
      } catch (e: any) {
        console.error('❌ Erro em useCourseAndModules:', e);
        setError(e);
        
        // Fallback para dados mock se Supabase falhar
        console.log('🔄 Usando fallback mock...');
        const fallbackTitle = courseSlug ? 
          courseSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
          'Curso Padrão';
        
        setCourse({
          id: 1,
          slug: courseSlug || 'default',
          title: fallbackTitle,
          summary: 'Curso de exemplo',
          status: 'active'
        });
        
        setModules([
          {
            id: 1,
            slug: 'modulo-1',
            title: 'Módulo 1: Introdução',
            summary: 'Módulo de exemplo',
            status: 'active',
            order: 1
          }
        ]);
      } finally {
        setLoading(false);
        console.log('🏁 useCourseAndModules concluído');
      }
    })();
  }, [tenantId, courseSlug]);

  // Loading total inclui tenant + dados
  const totalLoading = tenantLoading || loading;
  const totalError = tenantError || error;

  return { 
    tenantId, 
    course, 
    modules, 
    loading: totalLoading, 
    error: totalError 
  };
}

export function useCourseWithModulesAndLessons(courseSlug?: string) {
  const { tenantId, loading: tenantLoading, error: tenantError } = useTenantId();
  const [course, setCourse] = React.useState<CourseLite | null>(null);
  const [modulesWithLessons, setModulesWithLessons] = React.useState<Array<ModuleLite & { lessons: LessonLite[] }>>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    console.log('🔄 useCourseWithModulesAndLessons useEffect:', { tenantId, courseSlug });
    
    if (!tenantId) {
      console.log('⏳ Aguardando tenantId...');
      return;
    }
    
    if (!courseSlug) {
      console.log('⚠️ courseSlug não fornecido, usando fallback');
      setCourse({
        id: 1,
        slug: 'default',
        title: 'Curso Padrão',
        summary: 'Curso de exemplo',
        status: 'active'
      });
      
      setModulesWithLessons([
        {
          id: 1,
          slug: 'modulo-1',
          title: 'Módulo 1: Introdução',
          summary: 'Módulo de exemplo',
          status: 'active',
          lessons: []
        }
      ]);
      return;
    }
    
    (async () => {
      try {
        console.log('🚀 Iniciando busca de curso, módulos e lições...');
        setLoading(true);
        setError(null);
        
        // Buscar curso
        console.log('📚 Buscando curso:', courseSlug);
        const c = await getCourseBySlug(tenantId, courseSlug);
        console.log('✅ Curso encontrado:', c);
        
        // Verificar se o curso tem título válido
        if (!c?.title) {
          console.warn('⚠️ Curso encontrado mas sem título, usando fallback');
          const fallbackTitle = courseSlug ? 
            courseSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
            'Curso Padrão';
          c.title = fallbackTitle;
        }
        
        setCourse(c);
        
        // Buscar módulos
        console.log('📖 Buscando módulos para curso:', c.id);
        const ms = await listModules(tenantId, c.id);
        console.log('✅ Módulos encontrados:', ms);
        
        // Buscar lições para cada módulo em paralelo
        console.log('📚 Buscando lições para todos os módulos...');
        const lessonsPromises = ms.map(module => 
          listLessonsByModule(module.id).catch(err => {
            console.warn(`⚠️ Erro ao buscar lições do módulo ${module.id}:`, err);
            return []; // Retornar array vazio se houver erro
          })
        );
        
        const lessonsResults = await Promise.all(lessonsPromises);
        console.log('✅ Lições carregadas para todos os módulos');
        
        // Anexar lições aos módulos
        const modulesWithLessonsData = ms.map((module, index) => ({
          ...module,
          lessons: lessonsResults[index] || []
        }));
        
        console.log('✅ Módulos com lições preparados:', modulesWithLessonsData);
        setModulesWithLessons(modulesWithLessonsData);
        
      } catch (e: any) {
        console.error('❌ Erro em useCourseWithModulesAndLessons:', e);
        setError(e);
        
        // Fallback para dados mock se Supabase falhar
        console.log('🔄 Usando fallback mock...');
        const fallbackTitle = courseSlug ? 
          courseSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
          'Curso Padrão';
        
        setCourse({
          id: 1,
          slug: courseSlug || 'default',
          title: fallbackTitle,
          summary: 'Curso de exemplo',
          status: 'active'
        });
        
        setModulesWithLessons([
          {
            id: 1,
            slug: 'modulo-1',
            title: 'Módulo 1: Introdução',
            summary: 'Módulo de exemplo',
            status: 'active',
            order: 1,
            lessons: []
          }
        ]);
      } finally {
        setLoading(false);
        console.log('🏁 useCourseWithModulesAndLessons concluído');
      }
    })();
  }, [tenantId, courseSlug]);

  // Loading total inclui tenant + dados
  const totalLoading = tenantLoading || loading;
  const totalError = tenantError || error;

  return { 
    tenantId, 
    course, 
    modules: modulesWithLessons, 
    loading: totalLoading, 
    error: totalError 
  };
}

export function useLessons(moduleId?: string) {
  const [lessons, setLessons] = React.useState<LessonLite[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!moduleId) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const ls = await listLessonsByModule(moduleId);
        setLessons(ls);
      } catch (e: any) {
        console.error('❌ Erro em useLessons:', e);
        setError(e);
        // Fallback para dados mock
        setLessons([
          {
            id: 1,
            module_id: moduleId,
            title: 'Lição de Exemplo',
            description: 'Lição de exemplo',
            order: 1,
            is_free: true
          }
        ]);
      } finally {
        setLoading(false);
      }
    })();
  }, [moduleId]);

  return { lessons, loading, error };
}

export function useLesson(lessonId?: string) {
  const [lesson, setLesson] = React.useState<LessonLite | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    console.log('🔍 useLesson useEffect:', { lessonId });
    
    if (!lessonId) {
      console.log('⚠️ useLesson: lessonId não fornecido');
      return;
    }
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('📚 Buscando lição:', lessonId);
        const l = await getLessonById(lessonId);
        console.log('✅ Lição encontrada:', l);
        setLesson(l);
      } catch (e: any) {
        console.error('❌ Erro em useLesson:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId]);

  return { lesson, loading, error };
}

export function useModuleBySlug(tenantId?: string, courseId?: string, moduleSlug?: string) {
  const [module, setModule] = React.useState<ModuleLite | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    console.log('🔍 useModuleBySlug useEffect:', { tenantId, courseId, moduleSlug });
    
    if (!tenantId || !courseId || !moduleSlug) {
      console.log('⚠️ useModuleBySlug: Parâmetros insuficientes');
      return;
    }
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('📖 Buscando módulo:', { tenantId, courseId, moduleSlug });
        const m = await getModuleBySlug(tenantId, courseId, moduleSlug);
        console.log('✅ Módulo encontrado:', m);
        setModule(m);
      } catch (e: any) {
        console.error('❌ Erro em useModuleBySlug:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, courseId, moduleSlug]);

  return { module, loading, error };
}

export function useCourses() {
  const { tenantId, loading: tenantLoading, error: tenantError } = useTenantId();
  const [courses, setCourses] = React.useState<CourseLite[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!tenantId) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const cs = await listCourses(tenantId);
        setCourses(cs);
      } catch (e: any) {
        console.error('❌ Erro em useCourses:', e);
        setError(e);
        // Fallback para dados mock
        setCourses([
          {
            id: 1,
            slug: 'curso-padrao',
            title: 'Curso Padrão',
            summary: 'Curso de exemplo',
            status: 'active'
          }
        ]);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  const totalLoading = tenantLoading || loading;
  const totalError = tenantError || error;

  return { 
    tenantId, 
    courses, 
    loading: totalLoading, 
    error: totalError 
  };
}

// Debug hook to list all lessons
export function useAllLessons() {
  const [lessons, setLessons] = React.useState<LessonLite[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!isSupabase()) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const ls = await listAllLessons();
        setLessons(ls);
      } catch (e: any) {
        console.error('❌ Erro em useAllLessons:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { lessons, loading, error };
}
